import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { z } from "zod";

import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ProtectedRoute } from "@/components/protected-route";
import { requireFeature } from "@/lib/feature-gate";
import instance from "@/lib/axios";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";

import { ProjectTable } from "@/components/crud/table/ProjectTable";
import { ProjectFormSheet } from "@/components/crud/forms/ProjectFormSheet";
import { ProjectDetailsSheet } from "@/components/crud/forms/ProjectDetailsSheet";
import useFetchProjects from "@/hooks/api/projects/useFetchProject";
import useCreateProject from "@/hooks/api/projects/useCreateProject";
import useUpdateProject from "@/hooks/api/projects/useUpdateProject";
import useDeleteProject from "@/hooks/api/projects/useDeleteProject";
import useAddProjectMember from "@/hooks/api/projects/useAddProjectMember";
import useRemoveProjectMember from "@/hooks/api/projects/useRemoveProjectMember";
import { PROJECT_STATUS_OPTIONS } from "@/schemas/project/project.schema";
import { PROJECT_STATUS_CONFIG_MAP } from "@/components/crud/table/ProjectTable";
import type { ApiUser } from "@/types/shift-assignment";
import type { Project, ProjectStatus } from "@/types/project";
import type { ProjectFormValues } from "@/schemas/project/project.schema";

const projectSearchSchema = z.object({
  status: z.enum(PROJECT_STATUS_OPTIONS).optional().catch(undefined),
});

export const Route = createFileRoute("/admin/_layout/projects")({
  beforeLoad: () => requireFeature("projects"),
  validateSearch: projectSearchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <ProtectedRoute
      requiredPermission={["project.project.view", "project.project.manage"]}
    >
      <AdminProjectsPage />
    </ProtectedRoute>
  );
}

function AdminProjectsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManageProjects = can(capabilities, "project.project.manage");
  const { page, setPage, pageSize, handlePageSizeChange } = usePaginationState();
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [allUsers, setAllUsers] = useState<ApiUser[]>([]);

  // Owner search-as-you-type -- mirrors TaskDataTable's own autoComplete
  // wiring exactly, so ProjectFormSheet's Owner field behaves like Task's
  // User field.
  const [autoCompleteSearch, setAutoCompleteSearch] = useState("");
  const autoCompleteQuery = useAutoCompleteUser({
    search: autoCompleteSearch || undefined,
  });
  const ownerSearchResults = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data],
  );
  const handleSearchUsers = useCallback((query: string) => {
    setAutoCompleteSearch(query);
  }, []);

  const projectsQuery = useFetchProjects(
    {
      page,
      page_size: pageSize,
      status: statusFilter ? (statusFilter as ProjectStatus) : undefined,
    },
    true,
  );
  const projects = projectsQuery.data?.results ?? [];
  const count = projectsQuery.data?.count ?? 0;

  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();

  const isMutating =
    createProject.isPending || updateProject.isPending || deleteProject.isPending;

  // Plain org-users endpoint, not a project-domain hook -- same pattern as
  // the Deployment Manifest form's custodian/task pickers. Feeds the
  // Members multi-select, which filters client-side over the full roster.
  useEffect(() => {
    instance
      .get("/api/users/", { params: { page_size: 100 } })
      .then((res) => setAllUsers(res.data?.results ?? res.data ?? []))
      .catch(() => toast.error("Failed to load users"));
  }, []);

  const handleSubmit = async (data: ProjectFormValues) => {
    const { member_ids, ...payload } = data;
    if (editing) {
      await updateProject.mutateAsync({ id: editing.id, payload, isAdmin: true });
      const currentMemberIds = editing.memberships.map((m) => m.user.id);
      const nextMemberIds = member_ids ?? [];
      const toAdd = nextMemberIds.filter((id) => !currentMemberIds.includes(id));
      const toRemove = currentMemberIds.filter((id) => !nextMemberIds.includes(id));
      await Promise.all([
        ...toAdd.map((userId) =>
          addMember.mutateAsync({ id: editing.id, userId, isAdmin: true }),
        ),
        ...toRemove.map((userId) =>
          removeMember.mutateAsync({ id: editing.id, userId, isAdmin: true }),
        ),
      ]);
      toast.success("Project updated successfully!");
    } else {
      const created = await createProject.mutateAsync({
        payload,
        isAdmin: true,
      });
      await Promise.all(
        (member_ids ?? []).map((userId) =>
          addMember.mutateAsync({ id: created.id, userId, isAdmin: true }),
        ),
      );
      toast.success("Project created successfully!");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteProject.mutateAsync({ id: deleteTarget.id, isAdmin: true });
    toast.success("Project deleted successfully!");
    setDeleteTarget(null);
  };

  const handleStatusChange = async (project: Project, status: ProjectStatus) => {
    await updateProject.mutateAsync({
      id: project.id,
      payload: { status },
      isAdmin: true,
    });
    toast.success("Project updated successfully!");
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="w-full space-y-4 p-6 pt-0">
        <PageHeader
          title="Projects"
          description="Manage every project across the organization."
          onToggleFilter={() => setShowFilters((s) => !s)}
          onAdd={canManageProjects ? () => setIsSheetOpen(true) : undefined}
          addLabel="New Project"
          isLoading={projectsQuery.isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => setStatusFilter("")}
          isLoading={projectsQuery.isFetching}
        >
          <SelectFilter
            placeholder="Status"
            options={PROJECT_STATUS_OPTIONS.map((s) => ({
              label: PROJECT_STATUS_CONFIG_MAP[s].label,
              value: s,
            }))}
            value={statusFilter as ProjectStatus | undefined}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
        </FiltersPanel>

        <ProjectTable
          projects={projects}
          isLoading={isMutating || projectsQuery.isPending}
          onTitleClick={(p) =>
            navigate({ to: "/admin/projects/$id", params: { id: String(p.id) } })
          }
          onView={(p) => setViewingProject(p)}
          onEdit={(p) => {
            setEditing(p);
            setIsSheetOpen(true);
          }}
          onDelete={(p) => setDeleteTarget(p)}
          onStart={(p) => handleStatusChange(p, "active")}
          onHold={(p) => handleStatusChange(p, "on_hold")}
          onResume={(p) => handleStatusChange(p, "active")}
          onComplete={(p) => handleStatusChange(p, "completed")}
          onCancel={(p) => handleStatusChange(p, "cancelled")}
          canManage={canManageProjects}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={projectsQuery.isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <ProjectFormSheet
        isOpen={isSheetOpen}
        onClose={() => {
          setIsSheetOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        isLoading={isMutating}
        users={ownerSearchResults}
        onSearchUsers={handleSearchUsers}
        allUsers={allUsers}
        editingProject={editing}
      />

      {viewingProject && (
        <ProjectDetailsSheet
          isOpen={true}
          onClose={() => setViewingProject(null)}
          project={viewingProject}
          onChangeStatus={(status) => handleStatusChange(viewingProject, status)}
          actionLoading={updateProject.isPending}
          canManage={canManageProjects}
        />
      )}

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete project?"
        description="This cannot be undone. Tasks linked to this project will be unlinked, not deleted."
        itemName={deleteTarget?.name}
        isLoading={deleteProject.isPending}
      />
    </SidebarInset>
  );
}
